import React, { useEffect, useState } from "react";
import { Pagination, Stack } from "@mui/material";
import { Loader } from "../loader";
import moment from "moment";
import api from "../../../api";
import './MemberFeedback.css'

// Lucide Icons
import { 
  Search, 
  RotateCcw, 
  Mail, 
  Star, 
  Zap, 
  ThumbsUp,
  Heart,
  TrendingUp,
  MessageSquare,
  Calendar,
  Filter,
  ChevronRight,
  ChevronLeft,
  HelpCircle,
  Lightbulb
} from "lucide-react";

function MemberFeedback({ match }) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState([]);
  const [noOfPost, setnoOfPost] = useState(0);
  const [loader, setLoader] = useState(true);
  const [resetPage, setResetPage] = useState(false);
  const [searchMail, setSearchMail] = useState("");

  const getPosts = async () => {
    try {
      let payload = {
        page: page,
      };
      setLoader(true);
      const postData = await api.memberFeedbackGet(payload);
      setData(postData.post);
      setnoOfPost(postData.count);
      setLimit(postData.limit);
      setLoader(false);
    } catch (error) {
      console.log(error);
      setLoader(false);
    }
  };

  useEffect(() => {
    getPosts();
  }, [page, resetPage]);

  const handleSearch = async () => {
    if (searchMail) {
      let payload = {
        email: searchMail,
      };
      const res = await api.memberFeedbackSeacrh(payload);
      if (res?.success) {
        setData(res?.feedback);
      } else {
        alert("Email address not found");
      }
    } else {
      alert("Please insert email for search");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleReset = () => {
    setResetPage(!resetPage);
    setPage(1);
    setSearchMail("");
  };

  return (
    <>
      <div className="feedback-container">
        {/* Header matching the design */}
        <div className="feedback-header-section">
          <div className="feedback-header-content">
            <div className="members-header-title-wrapper">
              <h1 className="header-title">Member Feedback</h1>
              <span className="feedback-count-badge">{noOfPost}</span>
            </div>
            <p className="header-subtitle">Track and analyze member feedback and suggestions</p>
          </div>
          
          <div className="feedback-search-section">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search by email..."
                value={searchMail}
                onChange={(e) => setSearchMail(e.target.value)}
                onKeyPress={handleKeyPress}
                className="feedback-search-input"
              />
              <button 
                className="feedback-search-btn"
                onClick={handleSearch}
              >
                Search
              </button>
            </div>
            <button 
              className="feedback-reset-btn"
              onClick={handleReset}
            >
              <RotateCcw size={16} />
              Reset
            </button>
          </div>
        </div>

        <div className="content-card">
          <div className="table-header">
            <div className="table-info">
              <Filter size={18} />
              <span>SHOWING {data.length} OF {noOfPost} RECORDS</span>
            </div>
          </div>

          <Table 
            data={data} 
            page={page} 
            setPage={setPage} 
            noOfPost={noOfPost} 
            limit={limit} 
          />
        </div>
      </div>

      {loader && <Loader />}
      
    </>
  );
}

const Table = ({ data, page, setPage, noOfPost, limit }) => {
  const handleChange = (event, value) => {
    setPage(value);
  };

  const formatDate = (date) => {
    let dateTemp = moment(date).format("MMMM Do YYYY, h:mm:ss a");
    return dateTemp;
  };

  const getSatisfactionColor = (level) => {
    switch(level) {
      case 'Very Satisfied': return '#059669';
      case 'Satisfied': return '#10b981';
      case 'Neutral': return '#6b7280';
      case 'Dissatisfied': return '#f59e0b';
      case 'Very Dissatisfied': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const getRecommendationColor = (recommend) => {
    switch(recommend) {
      case 'Yes': return '#059669';
      case 'No': return '#dc2626';
      case 'Maybe': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const renderStarRating = (rating, maxRating = 5) => {
    return (
      <div className="star-rating">
        {[...Array(maxRating)].map((_, i) => (
          <Star
            key={i}
            size={12}
            fill={i < rating ? "#f59e0b" : "#e5e7eb"}
            color={i < rating ? "#f59e0b" : "#d1d5db"}
          />
        ))}
        <span className="rating-text">{rating}/{maxRating}</span>
      </div>
    );
  };

  return (
    <>
      <div className="feedback-table-container">
        <div className="table-wrapper">
          <table className="feedback-table">
            <thead>
              <tr>
                <th>
                  <div className="table-header-cell">
                    <Mail size={16} />
                    <span>EMAIL</span>
                  </div>
                </th>
                <th>
                  <div className="table-header-cell">
                    <Star size={16} />
                    <span>SATISFACTION LEVEL</span>
                  </div>
                </th>
                <th>
                  <div className="table-header-cell">
                    <Zap size={16} />
                    <span>FEATURE EXPLORED</span>
                  </div>
                </th>
                <th>
                  <div className="table-header-cell">
                    <TrendingUp size={16} />
                    <span>FEATURE RATING</span>
                  </div>
                </th>
                <th>
                  <div className="table-header-cell">
                    <ThumbsUp size={16} />
                    <span>RECOMMEND OTHERS</span>
                  </div>
                </th>
                <th>
                  <div className="table-header-cell">
                    <Heart size={16} />
                    <span>LIKE MOST</span>
                  </div>
                </th>
                <th>
                  <div className="table-header-cell">
                    <HelpCircle size={16} />
                    <span>HOW WE IMPROVE</span>
                  </div>
                </th>
                <th>
                  <div className="table-header-cell">
                    <Lightbulb size={16} />
                    <span>SUGGESTIONS</span>
                  </div>
                </th>
                <th>
                  <div className="table-header-cell">
                    <Calendar size={16} />
                    <span>SUBMITTED AT</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((item, key) => (
                  <tr key={key} className="feedback-row">
                    <td>
                      <div className="data-cell">
                        <span className="data-value email-value">{item?.user?.email || "-"}</span>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell">
                        <span 
                          className="satisfaction-badge"
                          style={{ 
                            backgroundColor: getSatisfactionColor(item?.satisfaction_level),
                            color: 'white'
                          }}
                        >
                          {item?.satisfaction_level || "-"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell">
                        <div className="feature-list">
                          {item?.features_explored?.map((feature, i) => (
                            <div key={i} className="feature-tag">
                              {feature}
                            </div>
                          )) || "-"}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell">
                        <div className="feature-ratings">
                          {item?.feature_helpfulness && (
                            <>
                              <div className="rating-item">
                                <span className="rating-label">Crypto Trade Signals:</span>
                                {renderStarRating(item.feature_helpfulness.Crypto_Trade_Signals)}
                              </div>
                              <div className="rating-item">
                                <span className="rating-label">Stock & Commodity:</span>
                                {renderStarRating(item.feature_helpfulness.Stock_and_Commodity)}
                              </div>
                              <div className="rating-item">
                                <span className="rating-label">Market Updates:</span>
                                {renderStarRating(item.feature_helpfulness.Market_Updates)}
                              </div>
                              <div className="rating-item">
                                <span className="rating-label">Web3 Education:</span>
                                {renderStarRating(item.feature_helpfulness.Web3_Education)}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell">
                        <span 
                          className="recommendation-badge"
                          style={{ 
                            backgroundColor: getRecommendationColor(item?.recommend_others),
                            color: 'white'
                          }}
                        >
                          {item?.recommend_others || "-"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell">
                        <span className="data-value comment-text">
                          {item?.about_premium_comment || "-"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell">
                        <span className="data-value comment-text">
                          {item?.improve_experience || "-"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell">
                        <span className="data-value comment-text">
                          {item?.suggestions || "-"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell">
                        <span className="data-value timestamp">
                          {formatDate(item?.submitted_at) || "-"}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="empty-row">
                  <td colSpan="9">
                    <div className="empty-state">
                      <div className="empty-icon">
                        <MessageSquare size={48} />
                      </div>
                      <h3>No Member Feedback Found</h3>
                      <p>No member feedback is available at the moment</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {data.length > 0 && (
          <div className="table-footer">
            <div className="pagination-info">
              Showing page {page} of {Math.ceil(noOfPost / limit)}
            </div>
            <div className="pagination-controls">
              <button 
                className="pagination-button prev-button"
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
              >
                <ChevronLeft size={18} />
                Previous
              </button>
              
              <Stack spacing={2}>
                <Pagination
                  count={Math.ceil(noOfPost / limit)}
                  page={page}
                  onChange={handleChange}
                  shape="rounded"
                  color="primary"
                  size="medium"
                />
              </Stack>
              
              <button 
                className="pagination-button next-button"
                onClick={() => setPage(page + 1)}
                disabled={page >= Math.ceil(noOfPost / limit)}
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

    </>
  );
};

export default MemberFeedback;